import { useMemo, useState } from 'react';
import type { DocListItem } from './types';

export interface DocFilters {
  query: string;
  setQuery: (q: string) => void;
  /** Selected org, or 'all'. */
  org: string;
  setOrg: (o: string) => void;
  /** Distinct owners across the items, sorted — the org select's options.
   *  Empty when no item carries an owner (the caller then shows no select). */
  orgs: string[];
  /** Items surviving the query + org filters, in their INPUT order. */
  filtered: DocListItem[];
}

/**
 * Filter state for a document listing: a name/location query and an org select.
 *
 * Filtering only ever REMOVES items — it never reorders them. Once results
 * arrive server-ranked (semantic search, sdx#1119), the incoming order *is* the
 * relevance order, and a client-side sort here would silently discard it. So
 * `filtered` is always a subsequence of the input in the same order.
 */
export function useDocFilters(items: DocListItem[]): DocFilters {
  const [query, setQuery] = useState('');
  const [org, setOrg] = useState('all');

  const orgs = useMemo(
    () => Array.from(new Set(items.map((i) => i.owner).filter((o): o is string => !!o)))
      .sort((a, b) => a.localeCompare(b)),
    [items],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (org !== 'all' && item.owner !== org) return false;
      if (q && !item.title.toLowerCase().includes(q) && !item.location.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, query, org]);

  return { query, setQuery, org, setOrg, orgs, filtered };
}
